import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "notistack";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import usePageTitle from "../utils/usePageTitle";

const REMEMBER_KEY = "remember_login_v1";
const MIN_PASSWORD_LEN = 6;

const SELECTED_AGENCY_KEY = "selected_agency";
const AGENCY_ID_KEY = "agencyId";

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function getPasswordStrength(pw) {
  const p = pw || "";
  let score = 0;

  if (p.length >= 8) score += 1;
  if (/[A-Z]/.test(p)) score += 1;
  if (/[a-z]/.test(p)) score += 1;
  if (/[0-9]/.test(p)) score += 1;
  if (/[^A-Za-z0-9]/.test(p)) score += 1;

  const percent = Math.min(100, Math.round((score / 5) * 100));

  let label = "Faible";
  if (percent >= 60) label = "Moyen";
  if (percent >= 80) label = "Fort";

  return { percent, label };
}

const storeAssignedAgency = (user) => {
  try {
    if (!user?.agency_id) return;

    localStorage.setItem(AGENCY_ID_KEY, String(user.agency_id));
    localStorage.setItem(
      SELECTED_AGENCY_KEY,
      JSON.stringify({
        id: user.agency_id,
        name: user.agency_name || user.agency || "",
        city: user.city || user.location_city || "",
        country: user.country || user.location_country || "",
      })
    );
  } catch {
    // ignore
  }
};

const clearStoredAgency = () => {
  try {
    localStorage.removeItem(AGENCY_ID_KEY);
    localStorage.removeItem(SELECTED_AGENCY_KEY);
  } catch {
    // ignore
  }
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [role, setRole] = useState("agent");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [touched, setTouched] = useState({ email: false, password: false });

  usePageTitle("Connexion");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (!raw) return;

      const saved = JSON.parse(raw);
      if (saved?.email) setEmail(saved.email);
      if (saved?.role) setRole(saved.role);
      if (typeof saved?.rememberMe === "boolean") {
        setRememberMe(saved.rememberMe);
      }
    } catch {
      // ignore
    }
  }, []);

  const title = useMemo(() => {
    return role === "admin" ? "Connexion Admin" : "Connexion Agent";
  }, [role]);

  const subtitle = "Connectez-vous pour accéder à votre tableau de bord.";
  const endpoint = "/users/login";

  const emailSoftError =
    touched.email && email.trim() && !isValidEmail(email)
      ? "Email invalide (format attendu: nom@domaine.com)"
      : "";

  const passwordSoftError =
    touched.password && password && password.length < MIN_PASSWORD_LEN
      ? `Mot de passe trop court (au moins ${MIN_PASSWORD_LEN} caractères)`
      : "";

  const { percent: pwPercent, label: pwLabel } = useMemo(
    () => getPasswordStrength(password),
    [password]
  );

  const persistRememberMe = (nextRememberMe, nextEmail, nextRole) => {
    try {
      if (!nextRememberMe) {
        localStorage.removeItem(REMEMBER_KEY);
        return;
      }

      localStorage.setItem(
        REMEMBER_KEY,
        JSON.stringify({
          rememberMe: nextRememberMe,
          email: (nextEmail || "").trim(),
          role: nextRole,
        })
      );
    } catch {
      // ignore
    }
  };

  const handleLogin = async () => {
    if (isSubmitting) return;

    setMessage(null);
    setTouched({ email: true, password: true });

    if (!email.trim() || !password) {
      setMessage({
        type: "error",
        text: "Veuillez saisir votre email et votre mot de passe.",
      });
      return;
    }

    if (!isValidEmail(email)) {
      setMessage({
        type: "error",
        text: "Veuillez vérifier le format de votre email.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await axios.post(endpoint, {
        email: email.trim().toLowerCase(),
        password,
        expectedRole: role,
      });

      const { token, user } = res.data;

      if (!token || !user) {
        throw new Error("Réponse de connexion invalide.");
      }

      if (user.role === "agent" && !user.agency_id) {
        clearStoredAgency();
        setMessage({
          type: "error",
          text: "Aucune agence ne vous est assignée. Contactez l’administrateur.",
        });
        enqueueSnackbar("Aucune agence ne vous est assignée. Contactez l’administrateur.", {
          variant: "error",
        });
        return;
      }

      login(token, user);
      persistRememberMe(rememberMe, email, role);

      if (user?.agency_id) {
        storeAssignedAgency(user);
      } else {
        clearStoredAgency();
      }

      if (user?.role === "agent") {
        navigate("/transactions");
        return;
      }

      if (user?.role === "admin") {
        navigate("/admin/analytics");
        return;
      }

      navigate("/");
    } catch (err) {
      console.error("Login error:", err);

      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Échec de la connexion. Veuillez réessayer.";

      setMessage({
        type: "error",
        text: serverMessage,
      });

      enqueueSnackbar(serverMessage, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onEnterSubmit = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background:
          "radial-gradient(1200px 600px at 20% 10%, rgba(25,118,210,0.15), transparent 60%)," +
          "radial-gradient(1000px 500px at 90% 30%, rgba(46,125,50,0.12), transparent 60%)," +
          "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.00))",
        py: 6,
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={6} sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <LockOutlinedIcon />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Rôle</InputLabel>
            <Select
              value={role}
              label="Rôle"
              onChange={(e) => {
                setRole(e.target.value);
                persistRememberMe(rememberMe, email, e.target.value);
              }}
              disabled={isSubmitting}
            >
              <MenuItem value="agent">Agent</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Email"
            fullWidth
            sx={{ mb: 2 }}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (rememberMe) persistRememberMe(true, e.target.value, role);
            }}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            onKeyDown={onEnterSubmit}
            autoComplete="email"
            disabled={isSubmitting}
            error={Boolean(emailSoftError)}
            helperText={emailSoftError || " "}
          />

          <TextField
            label="Mot de passe"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            onKeyDown={onEnterSubmit}
            autoComplete="current-password"
            disabled={isSubmitting}
            error={Boolean(passwordSoftError)}
            helperText={passwordSoftError || " "}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"
                    }
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ mt: 0.5, mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
              <Typography variant="body2" color="text.secondary">
                Robustesse : <b>{pwLabel}</b>
              </Typography>
              <Tooltip title="Ceci est un indicateur visuel uniquement. La validation réelle est faite par le serveur.">
                <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <LinearProgress variant="determinate" value={pwPercent} />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setRememberMe(next);
                    persistRememberMe(next, email, role);
                  }}
                />
              }
              label="Se souvenir de moi"
            />

            <Button
              component={Link}
              to="/forgot-password"
              variant="text"
              size="small"
              sx={{ textTransform: "none" }}
              disabled={isSubmitting}
            >
              Mot de passe oublié ?
            </Button>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleLogin}
            disabled={isSubmitting}
            sx={{ py: 1.2, fontWeight: 800, borderRadius: 2 }}
          >
            {isSubmitting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                Connexion...
              </Box>
            ) : (
              "Se connecter"
            )}
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2, textAlign: "center" }}
          >
            Astuce : appuyez sur <b>Entrée</b> pour continuer.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;